import React, { useState, useEffect, useRef } from 'react';
import GlassButton from './GlassButton';
import { exportToCSV, exportToExcel, formatDateForInput, parseDateLocal } from '../utils/scheduleHelpers';

/**
 * Панель инструментов для графиков (glass-стиль)
 */
const ScheduleToolbar = ({
  schedules,
  columns,
  filename,
  onAddRow,
  onRefresh,
  canEdit = false,
  scheduleType = 'schedule',
  cities = [],
  selectedCity = null,
  showCalendar = false,
  onToggleCalendar = null,
  prominentCalendar = true,
  tzCalendarModes = null,
  calendarMode = null,
  onCalendarModeChange = null,
  extraActions = [],
}) => {
  const [showExportMenu, setShowExportMenu] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowExportMenu(false);
      }
    };

    if (showExportMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showExportMenu]);

  const getExportFilename = (ext) => {
    const city = cities.find((c) => c.id === selectedCity);
    const cityName = city ? city.name : 'all';
    const date = formatDateForInput(new Date());
    return `${filename}_${cityName}_${date}.${ext}`;
  };

  const handleExportCSV = () => {
    exportToCSV(schedules, columns, getExportFilename('csv'));
    setShowExportMenu(false);
  };

  const handleExportExcel = () => {
    const sheetName = getScheduleTypeName(scheduleType);
    exportToExcel(schedules, columns, getExportFilename('xlsx'), sheetName);
    setShowExportMenu(false);
  };

  const size = prominentCalendar ? 'lg' : 'md';
  const visibleExtras = extraActions.filter((a) => !a.hidden);

  return (
    <div className="schedule-toolbar glass-panel">
      <div className="schedule-toolbar__left">
        <div className={`glass-chip${size === 'lg' ? '' : ' glass-chip--sm'}`}>
          <span style={{ fontSize: size === 'lg' ? 18 : 16 }}>📋</span>
          <span>
            Записей: <strong style={{ color: 'var(--text-primary)' }}>{schedules.length}</strong>
          </span>
        </div>
        <ScheduleStats schedules={schedules} />
      </div>

      <div className="schedule-toolbar__actions">
        {onRefresh && (
          <GlassButton onClick={onRefresh} icon="🔄" label="Обновить" variant="muted" size={size} title="Обновить данные" />
        )}

        <div style={{ position: 'relative' }} ref={menuRef}>
          <GlassButton
            onClick={() => setShowExportMenu(!showExportMenu)}
            icon="📥"
            label="Экспорт"
            variant="export"
            size={size}
            active={showExportMenu}
          />

          {showExportMenu && (
            <div
              className="glass-dropdown"
              style={{
                position: 'absolute',
                top: '100%',
                right: 0,
                marginTop: 8,
                zIndex: 100,
                minWidth: 200,
              }}
            >
              <ExportMenuItem icon="📊" label="Экспорт в Excel" sublabel=".xlsx" onClick={handleExportExcel} />
              <ExportMenuItem icon="📄" label="Экспорт в CSV" sublabel=".csv" onClick={handleExportCSV} />
            </div>
          )}
        </div>

        {visibleExtras.map((action) => (
          <GlassButton
            key={action.id}
            onClick={action.onClick}
            icon={action.icon}
            label={action.label}
            variant={action.variant || 'info'}
            size={size}
            disabled={action.disabled}
            title={action.title}
          />
        ))}

        {tzCalendarModes && onCalendarModeChange && (
          <>
            {tzCalendarModes.map((mode) => (
              <GlassButton
                key={mode.id}
                onClick={() => onCalendarModeChange(calendarMode === mode.id ? null : mode.id)}
                icon={mode.icon}
                label={mode.label}
                variant="calendar"
                size={size}
                active={calendarMode === mode.id}
              />
            ))}
            {calendarMode && (
              <GlassButton
                onClick={() => onCalendarModeChange(null)}
                icon="📊"
                label="Таблица"
                variant="calendar"
                size={size}
                active={false}
              />
            )}
          </>
        )}

        {!tzCalendarModes && onToggleCalendar && (
          <GlassButton
            onClick={onToggleCalendar}
            icon={showCalendar ? '📊' : '📅'}
            label={showCalendar ? 'Таблица' : 'Календарь'}
            variant="calendar"
            size={size}
            active={showCalendar}
          />
        )}

        {canEdit && onAddRow && (
          <GlassButton onClick={onAddRow} icon="➕" label="Добавить строку" variant="add" size={size} />
        )}
      </div>
    </div>
  );
};

const STAT_TYPES = {
  completed: 'completed',
  inProgress: 'progress',
  overdue: 'overdue',
  notStarted: 'waiting',
};

const ScheduleStats = ({ schedules }) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const stats = schedules.reduce(
    (acc, s) => {
      const actualEnd = s.actual_end_date ? parseDateLocal(s.actual_end_date) : null;
      const plannedEnd = s.planned_end_date ? parseDateLocal(s.planned_end_date) : null;
      const actualStart = s.actual_start_date ? parseDateLocal(s.actual_start_date) : null;

      if (actualEnd) {
        acc.completed++;
        if (plannedEnd && actualEnd > plannedEnd) acc.delayed++;
      } else if (actualStart) {
        acc.inProgress++;
        if (plannedEnd && today > plannedEnd) acc.overdue++;
      } else {
        acc.notStarted++;
      }
      return acc;
    },
    { completed: 0, inProgress: 0, notStarted: 0, delayed: 0, overdue: 0 }
  );

  if (schedules.length === 0) return null;

  return (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
      {stats.completed > 0 && (
        <StatBadge icon="✓" count={stats.completed} label="завершено" type={STAT_TYPES.completed} />
      )}
      {stats.inProgress > 0 && (
        <StatBadge icon="🔄" count={stats.inProgress} label="в работе" type={STAT_TYPES.inProgress} />
      )}
      {stats.overdue > 0 && (
        <StatBadge icon="🔴" count={stats.overdue} label="просрочено" type={STAT_TYPES.overdue} />
      )}
      {stats.notStarted > 0 && (
        <StatBadge icon="📋" count={stats.notStarted} label="ожидает" type={STAT_TYPES.notStarted} />
      )}
    </div>
  );
};

const StatBadge = ({ icon, count, label, type }) => (
  <span className={`glass-chip glass-chip--sm glass-stat glass-stat--${type}`}>
    <span>{icon}</span>
    <span style={{ fontWeight: 600 }}>{count}</span>
    <span style={{ opacity: 0.85 }}>{label}</span>
  </span>
);

const ExportMenuItem = ({ icon, label, sublabel, onClick }) => (
  <button type="button" className="glass-dropdown__item" onClick={onClick}>
    <span style={{ fontSize: 18, width: 24, textAlign: 'center' }}>{icon}</span>
    <div style={{ flex: 1 }}>
      <div style={{ fontWeight: 500 }}>{label}</div>
      {sublabel && (
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{sublabel}</div>
      )}
    </div>
  </button>
);

const getScheduleTypeName = (type) => {
  const names = {
    construction: 'Строительство',
    document: 'Документация',
    procurement: 'Закупки',
    hr: 'HR',
    marketing: 'Маркетинг',
    directive: 'Директивный',
    preconstruction: 'ТЗ до строительства',
  };
  return names[type] || 'График';
};

export default ScheduleToolbar;
